<template>
  <div>
    <PageTitle class="_mts">
      <template #pageTitleLeft>
        <h1>
        <font-awesome-icon :icon="['fa', 'edit']" class='_mrxs' aria-hidden="true" role="presentation"/>
          {{fullName}}
          <strong> / Assign Group Access</strong>
        </h1>
      </template>
      <template #pageTitleRight>
        <router-link class="btn white --sm" :to="cancelRoute">Cancel</router-link>
      </template>
    </PageTitle>

    <CollapsibleSection class='_ptxs _pb0' :collapsible="false">
      <template #headerText>
        <p>Group Selection</p>
      </template>
      <template #collapsibleSection>
        <div container>
          <form @submit.prevent="search">
            <MultiInputSearch
              :submitDisabled="isSearching"
              >
              <PlaceholderInput label="Group name" name="searchGroup" v-model="searchName" class="-full-width" @submit="search" />
              <PlaceholderInput label="Group ID" name="searchGroupId" v-model="searchId" class="-full-width" @submit="search" />
            </MultiInputSearch>
            <CheckBox
              class="_mts"
              v-if="isInternalUser"
              label="Parent Group Search"
              valueKey="isParentGroupSearch"
              v-model="isParentGroupSearch" />
          </form>
          <AsyncLoader v-if="isSearching" />
          <AdvancedTable v-else title="Group Results" :headers="groupHeaders" class="_pts _pb0"
            id="search-groups" :items="filteredResults"
            :rowsPerPage="10" :clickable="true">
            <template #headers="{ headers, pagination, changeSort }" >
              <tr>
                <th
                  v-for="header in headers"
                  :key="header.text"
                  :class="['column sortable', pagination.descending ? 'desc' : 'asc', header.value === pagination.sortBy ? 'active' : '']"
                  @click="changeSort(header.value)"
                >
                  <div v-if="header.checkable">
                    <CheckBox
                      label=""
                      valueKey="checkAll"
                      @click="toggleAll($event)"
                      :value="allChecked" />
                  </div>
                  <div v-else>
                    <v-icon small>arrow_upward</v-icon>
                    {{ header.text }}
                  </div>
                </th>
              </tr>
            </template>
            <template #items='{ item, visibleHeaders, getCellValue }' >
              <tr>
                <td>
                  <CheckBox
                    label=""
                    :value="isSelected(item.groupCk)"
                    :valueKey="`check-${item.groupCk}`"
                    @click="toggleGroupSelect(item.groupCk, $event)"
                    />
                </td>
                <template v-for='(header, index) in visibleHeaders' >
                  <td :key='index' v-if="header.value !== 'checkbox'">{{getCellValue(item, header)}}</td>
                </template>
              </tr>
            </template>
          </AdvancedTable>

          <div class="_text-center">
            <AsyncButton
              type="submit"
              class="_mtxs --sm"
              :executing="addingGroups"
              @clicked="addGroups"
            >
              <template #buttonContent>
                <span>Submit</span>
              </template>
            </AsyncButton>
          </div>
        </div>
      </template>
    </CollapsibleSection>
  </div>
</template>

<script>
import { computed, onBeforeMount, reactive, ref, watch } from '@vue/composition-api';
import { useRoute, useRouter, useStore } from '@vue3shims';
import AdvancedTable from '@organisms/AdvancedTable.vue';
import AsyncLoader from '@molecules/AsyncLoader.vue';
import CollapsibleSection from '@sections/CollapsibleSection.vue';
import MultiInputSearch from '@molecules/MultiInputSearch.vue';
import PageTitle from '@organisms/PageTitle.vue';
import PlaceholderInput from '@atoms/PlaceholderInput.vue';
import CheckBox from '@atoms/Checkbox.vue';
import AsyncButton from '@molecules/AsyncButton.vue';
import { useHiddenRouteParams } from '@composables/useHiddenRouteParams';

export default {
  setup() {
    const route = useRoute();
    const router = useRouter();
    const store = useStore();
    const { hiddenRouteParams } = useHiddenRouteParams();
    const defaultAccess = computed(() => store.getters['minimumAccess']);
    const user = computed(() => store.getters['users2/activeUser']);

    const addGroups = () => {
      let access = JSON.parse(JSON.stringify(defaultAccess.value));
      // EM-2502 for client users, this should default to true
      // also for some reason the computed user isn't working here.  Not willing to sit and figure out why, tho.
      let usr = store.getters['users2/activeUser'];
      if(usr && usr.accountType === 'C') {
        access.createGroupUsersAllowed = true;
      }
      else {
        access.createGroupUsersAllowed = false;
      }
      // EM-1136  Primary Contact should be deselected by default:
      access.primaryContact = false;
      let groupsToAdd = [];
      selectedGroups.forEach((group) => {
        if(group.subGroups && group.subGroups.length > 0) {
          group.subGroups.forEach((sg) => {
            if(isInternalUser.value === true || sg.access.createGroupUsersAllowed) {
              groupsToAdd.push({
                access,
                groupCk: group.groupCk,
                groupId: group.groupId,
                groupName: group.groupName,
                parentGroupCk: group.parentGroupCk,
                parentGroupId: group.parentGroupId,
                status: sg.status,
                subGroupCk: sg.subGroupCk,
                subGroupEffectiveDate: sg.subGroupEffectiveDate,
                subGroupId: sg.subGroupId,
                subGroupName: sg.subGroupName,
                subGroupTerminationDate: sg.subGroupTerminationDate
              });
            }
          });
        }
        else {
          groupsToAdd.push(Object.assign(group, { access }));
        }
      });
      const user = Object.assign({}, store.getters['users2/activeUser']);
      const role = store.getters['users2/activeUserRole'];
      const subGroupCksUserHas = role.subGroupAccess.map(sg => sg.subGroupCk);
      const fGroups = groupsToAdd.filter((g) => !subGroupCksUserHas.includes(parseInt(g.subGroupCk)));
      // console.log(`fGroups`, fGroups);
      role.subGroupAccess = [...role.subGroupAccess, ...fGroups];
      user.role = role;
      store.commit('users2/SET_ACTIVE_USER', user);
      store.commit('users2/CLEAR_SEARCH_GROUPS');
      if(hiddenRouteParams.value.userId) {
        router.push({name: 'user-edit', params: {userId: hiddenRouteParams.value.userId }});
      }
      else {
        router.push({name: 'user-add'});
      }
    }
    const addingGroups = ref(false);
    const allChecked = computed(() => searchResults.value.length > 0 ? searchResults.value.length === selectedGroups.length : false);
    const cancelRoute = computed(() => {
      const route = { name: 'user-add' };
      if (user.value.userId && user.value.userId !== '') {
        route.name = 'user-edit';
        route.params = { userId: user.value.userId };
      }
      return route;
    });
    const changeSort = () => {

    }
    const filteredResults = computed(() => {
      let groups = searchResults.value;
      if(!isInternalUser.value) {
        // ext users can only add groups for which they have createGroupUsersAllowed
        groups = groups.filter((grp) => {
          if(grp.subGroups && grp.subGroups.length > 0) {
            return grp.subGroups.filter(sg => sg.access.createGroupUsersAllowed === true).length > 0;
          }
          else {
            return grp.access && grp.access.createGroupUsersAllowed;
          }
        });
      }
      if((searchName.value === '' && searchId.value === '') || (isInternalUser.value)) {
        return groups;
      }
      else {
        return groups.filter((r) => {
          return r.groupId.toLowerCase().indexOf(searchId.value.toLowerCase()) !== -1
          && r.groupName.toLowerCase().indexOf(searchName.value.toLowerCase()) !== -1;
        });
      }
    });
    const fullName = computed(() => {
      if(!user.value.userId || user.value.userId === '') {
        return 'New User';
      }
      else {
        return `${user.value.firstName} ${user.value.lastName}`;
      }
    });
    const groupHeaders = [
      { text: '', value: 'checkbox', sortable: false, checkable: true },
      { text: 'Group Name', align: 'left', value: 'groupName' },
      { text: 'Group ID', value: 'groupId', searchable: true },
      { text: 'Group CK', value: 'groupCk', visible: false }
    ];
    const isSelected = (groupCk) => selectedGroups.findIndex((g) => g.groupCk == groupCk) !== -1;
    const isInternalUser = computed(() => store.getters['isInternalUser']);
    const isParentGroupSearch = ref(false);
    const isSearching = ref(false);
    const search = async () => {
      if(!isInternalUser.value) {
        return;
      }
      isSearching.value = true;
      let opts = {
        isInternal: isInternalUser.value,
        isParentGroupSearch: isParentGroupSearch.value,
        name: searchName.value,
        id: searchId.value,
      }
      await store.dispatch('users2/searchGroupsForUserAdd', opts);
      isSearching.value = false;
    };
    const searchId = ref('');
    const searchName = ref('');
    const searchResults = computed(() => store.getters['users2/searchGroups']);
    let selectedGroups = reactive([]);

    const selectGroup = (groupCk) => {
      const group = searchResults.value.find((g) => g.groupCk === groupCk);
      if(group) {
        selectedGroups.push(group);
      }
    }
    const toggleAll = (selected) => {
      if(selected) {
        selectedGroups = [...filteredResults.value];
      }
      else {
        selectedGroups = [];
      }
    }
    const toggleGroupSelect = (groupCk, select) => {
      if(select) {
        selectGroup(groupCk);
      }
      else {
        unselectGroup(groupCk);
      }
    }
    const unselectGroup = (groupCk) => {
      const groupIdx = selectedGroups.findIndex((g) => g.groupCk === groupCk);
      if(groupIdx !== -1) {
        selectedGroups = selectedGroups.filter((g) => g.groupCk !== groupCk);
      }
    }

    onBeforeMount(async () => {
      if(hiddenRouteParams.value.userId && !user.value.userId) {
        try {
          await store.dispatch('users2/fetchUserWithRoleById', {userId: hiddenRouteParams.value.userId});
        } catch (error) {
          console.log(error);
          router.push({name: 'user-management'});
        }
      }
      if(isInternalUser.value === false) {
        let opts = {
          groups: store.getters['subGroupAccess'] || [],
          isParentGroupSearch: false,
          isInternalUser: false,
        }
        store.commit('users2/SET_SEARCH_GROUPS', opts);
      }
    });



    return {
      addGroups,
      addingGroups,
      allChecked,
      cancelRoute,
      changeSort,
      filteredResults,
      fullName,
      groupHeaders,
      isInternalUser,
      isParentGroupSearch,
      isSearching,
      isSelected,
      search,
      searchId,
      searchName,
      searchResults,
      selectedGroups,
      toggleAll,
      toggleGroupSelect,
      user,
    }

  },
  components: {
    AdvancedTable,
    AsyncButton,
    AsyncLoader,
    CheckBox,
    CollapsibleSection,
    MultiInputSearch,
    PageTitle,
    PlaceholderInput,
  }
}
</script>
